import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { toast } from '@/components/ui/Toast';
import { agentApi, type QuickNote } from '@/shared/api/agentApi';

/**
 * הערות בסגנון צ'אט על מספר טלפון: כל שורה נשלחת ב-Enter ונשמרת מיד כהערה
 * נפרדת (ContactNote) — בלי לחכות לסיכום מלא בסוף השיחה. הפיד מציג את
 * ההיסטוריה (מי כתב ומתי), כך שהנציג הבא רואה את כל ההתנהלות.
 */
export function NotesChat({ phone, maxHeight = 220 }: { phone: string; maxHeight?: number }) {
  const [notes, setNotes] = useState<QuickNote[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const feedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    void agentApi.quickNotes(phone)
      .then((r) => { if (alive) { setNotes(r.notes); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [phone]);

  // Stick to the newest note (chat-style, newest at the bottom).
  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [notes.length, loaded]);

  async function send() {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const note = await agentApi.addQuickNote(phone, body);
      setNotes((prev) => [...prev, note]);
      setDraft('');
    } catch {
      toast('שמירת ההערה נכשלה', 'danger');
    } finally {
      setSending(false);
    }
  }

  function when(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const time = d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
    return sameDay ? time : `${d.toLocaleDateString('he-IL')} · ${time}`;
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div
        ref={feedRef}
        style={{
          maxHeight, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6,
          padding: notes.length ? '8px 2px' : 0,
        }}
      >
        {!loaded ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>טוען הערות…</div>
        ) : notes.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
            אין הערות עדיין — כל שורה שתשלח נשמרת מיד.
          </div>
        ) : (
          notes.map((n) => (
            <div
              key={n.id}
              style={{
                alignSelf: 'flex-start', maxWidth: '92%',
                background: 'var(--surface-2)', border: '1px solid var(--line)',
                borderRadius: 'var(--r-3)', borderStartStartRadius: 'var(--r-1)',
                padding: '6px 10px',
              }}
            >
              <div style={{ fontSize: 'var(--t-sm)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{n.body}</div>
              <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>
                {n.user_name ? `${n.user_name} · ` : ''}{when(n.created_at)}
              </div>
            </div>
          ))
        )}
      </div>

      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder="הערה מהירה… Enter לשליחה"
          style={{ flex: 1 }}
          aria-label="הערה מהירה"
        />
        <button
          type="button"
          className="btn primary"
          onClick={() => void send()}
          disabled={sending || !draft.trim()}
          aria-label="שלח הערה"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Icon name="send" size={14} />
        </button>
      </div>
    </div>
  );
}
